// src/pwa.jsx — Phase 18 PWA installability helpers.
// Exports PWAInit, UpdateBanner, isStandalone, triggerInstall.
// Phase 27 Stage B: migrated from window-assign pattern to ES modules.
// Retains the 3 intentional window.__* bridges (__deferredInstallPrompt,
// __liftlogUpdateReady, __liftlogWaitingSW) — these are runtime coordination
// between the outside-React event handlers and React tree, not module deps.

// Stash for beforeinstallprompt — captured here in Phase 18, surfaced by Phase 21b.

import React from 'react';
import { LL } from './tokens.jsx';
import { isNative } from './native.js';
import { useApp } from './store.jsx';
import { useAnySheetOpen } from './sheet_history.js';

window.__deferredInstallPrompt = null;

function isStandalone() {
  try {
    if (typeof window === 'undefined') return false;
    // The Android / iOS app is "installed" by definition (no install prompt).
    if (isNative()) return true;
    // iOS Safari
    if (window.navigator && window.navigator.standalone === true) return true;
    // Everyone else
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) return true;
    return false;
  } catch (_) {
    return false;
  }
}

function triggerInstall() {
  const e = window.__deferredInstallPrompt;
  if (!e || typeof e.prompt !== 'function') return Promise.resolve({ outcome: 'unavailable' });
  e.prompt();
  return Promise.resolve(e.userChoice || { outcome: 'unknown' }).then((choice) => {
    // Event is single-use per Chrome spec.
    window.__deferredInstallPrompt = null;
    return choice;
  });
}

// ---- Safe reloads ----
// A new version must never reload the app mid-workout (typed weight lost, set
// interrupted) or under an open sheet. Reload requests from the service worker
// (RELOAD message, controllerchange) are parked until it is safe: no live
// workout, no sheet open, on a main tab — or the app is backgrounded with no
// live workout.
const TAB_ROOTS = new Set(['home', 'progress', 'programs', 'library', 'settings']);
const gate = { live: false, sheet: false, route: 'home', pending: false };
function safeToReload() { return !gate.live && !gate.sheet && TAB_ROOTS.has(gate.route); }
function requestReload() {
  if (safeToReload()) { window.location.reload(); return; }
  gate.pending = true;
}
function maybeRunPendingReload({ hidden = false } = {}) {
  if (!gate.pending) return;
  if (safeToReload() || (hidden && !gate.live)) { gate.pending = false; window.location.reload(); }
}
function liveSessionOf(state) {
  return !!(state && state.activeSessionId && (state.sessions || []).some(x => x.id === state.activeSessionId && !x.completedAt));
}

// PWAInit: render-less component that runs side effects on mount.
// - Registers sw.js once per session
// - Listens for `beforeinstallprompt` and stashes the event for Phase 21b
// - Detects a waiting SW and sets an updateReady flag the UpdateBanner reads
function PWAInit() {
  const [_, setTick] = React.useState(0);
  const { state, route } = useApp();
  const sheetOpen = useAnySheetOpen();
  gate.live = liveSessionOf(state);
  gate.sheet = sheetOpen;
  gate.route = (route && route.name) || 'home';
  // A parked reload runs as soon as the workout is over and the user is back on a tab.
  React.useEffect(() => { maybeRunPendingReload(); }, [gate.live, sheetOpen, gate.route]); // eslint-disable-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'hidden') maybeRunPendingReload({ hidden: true }); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  React.useEffect(() => {
    // The native app ships its web assets inside the APK / IPA: no service
    // worker (it would serve stale assets after an app update) and no install
    // prompt. Drop any worker left over from an earlier build.
    if (isNative()) {
      try { navigator.serviceWorker && navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister())).catch(() => {}); } catch (_) {}
      return undefined;
    }
    // beforeinstallprompt — stash for 21b to surface
    const onBIP = (e) => {
      // Do NOT call preventDefault() — Phase 21b adds that when it surfaces its UI.
      window.__deferredInstallPrompt = e;
    };
    window.addEventListener('beforeinstallprompt', onBIP);

    // SW register — non-blocking
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('./sw.js').then((reg) => {
        // Watch for waiting or a new worker ready to activate
        function watchWaiting() {
          if (reg.waiting) {
            window.__liftlogUpdateReady = true;
            // CRITICAL: record the WAITING worker here too. If the update was
            // already waiting at page load (the usual case after a reload),
            // `updatefound` never fires, so without this the banner's reload
            // would message the old controller instead of the new worker —
            // which never activates, and the banner loops forever.
            window.__liftlogWaitingSW = reg.waiting;
            setTick((t) => t + 1);
          }
        }
        watchWaiting();
        reg.addEventListener('updatefound', () => {
          const installing = reg.installing;
          if (!installing) return;
          installing.addEventListener('statechange', () => {
            if (installing.state === 'installed' && navigator.serviceWorker.controller) {
              // A new SW is installed alongside the active one.
              window.__liftlogUpdateReady = true;
              window.__liftlogWaitingSW = reg.waiting || installing;
              setTick((t) => t + 1);
            }
          });
        });
      }).catch(() => {
        // SW registration failed (file://, unsupported browser, etc.) — silent no-op.
      });

      // When the SW posts RELOAD back after SKIP_WAITING, reload.
      navigator.serviceWorker.addEventListener('message', (e) => {
        const data = (e && e.data) || {};
        if (data.type === 'RELOAD') requestReload();
      });

      // If controller changes (new SW took over via clients.claim), reload once.
      let reloading = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (reloading) return;
        reloading = true;
        requestReload();
      });
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', onBIP);
    };
  }, []);

  return null;
}

// UpdateBanner: renders when a waiting SW is detected. User-consent reload.
// Only shown when an update cannot interrupt anything: no live workout, no
// sheet or keypad open, on a main tab. It sits just above the tab bar, and
// only its "Update" button reloads (the rest of the card is inert).
function UpdateBanner() {
  const [ready, setReady] = React.useState(false);
  const [dismissed, setDismissed] = React.useState(false);
  const { state, route } = useApp();
  const sheetOpen = useAnySheetOpen();
  const live = liveSessionOf(state);
  const onTab = TAB_ROOTS.has((route && route.name) || '');
  const needsWelcome = !state.welcomed && (state.programs || []).length === 0;

  React.useEffect(() => {
    if (isNative()) return undefined; // app updates come from the store
    const id = setInterval(() => {
      if (window.__liftlogUpdateReady && !dismissed) setReady(true);
    }, 500);
    return () => clearInterval(id);
  }, [dismissed]);

  if (!ready || dismissed || live || sheetOpen || !onTab || needsWelcome) return null;

  const onReload = () => {
    const post = (sw) => { try { sw && sw.postMessage && sw.postMessage({ type: 'SKIP_WAITING' }); } catch (_) {} };
    try {
      if ('serviceWorker' in navigator) {
        // Always message the genuinely WAITING worker — never the controller
        // (messaging the active controller no-ops skipWaiting and reloads without
        // activating the new SW, which loops the banner). Query the registration
        // at click time as the authoritative source, plus the cached handle.
        post(window.__liftlogWaitingSW);
        navigator.serviceWorker.getRegistration()
          .then((reg) => { if (reg && reg.waiting) post(reg.waiting); })
          .catch(() => {});
      }
      // Safety timeout: if SW doesn't reload us in 1.5s, force reload.
      setTimeout(() => {
        try { window.location.reload(); } catch (_) {}
      }, 1500);
    } catch (_) {
      try { window.location.reload(); } catch (__) {}
    }
  };

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'absolute',
        // Above the tab bar (≈64px + safe area); never over a sheet or keypad
        // because it is not rendered while one is open.
        bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)',
        left: 12,
        right: 12,
        zIndex: 49,
        padding: '8px 8px 8px 14px',
        borderRadius: LL.r.md,
        background: LL.surface2,
        border: `1px solid color-mix(in oklab, ${LL.accent} 40%, ${LL.line})`,
        color: LL.fg,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontFamily: LL.ui,
        fontSize: LL.fs.meta,
        boxShadow: LL.shadow.md,
        animation: `toastIn ${LL.motion.base}ms ${LL.ease}`,
      }}
    >
      <span style={{ flex: 1, minWidth: 0, lineHeight: 1.35 }}>
        <span style={{ display: 'block', color: LL.accentText, fontWeight: 700 }}>New version ready</span>
        <span style={{ display: 'block', color: LL.fgMute, fontSize: LL.fs.sm }}>Your log is kept</span>
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        style={{
          height: 36, padding: '0 10px', borderRadius: LL.r.sm, border: 'none', background: 'transparent',
          color: LL.fgMute, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer',
        }}
      >Later</button>
      <button
        type="button"
        onClick={onReload}
        style={{
          height: 36, padding: '0 14px', borderRadius: LL.r.sm, border: 'none', background: LL.accent,
          color: LL.accentInk, fontFamily: LL.ui, fontSize: LL.fs.body, fontWeight: 600, cursor: 'pointer',
        }}
      >Update</button>
    </div>
  );
}

export { PWAInit, UpdateBanner, isStandalone, triggerInstall };
